import * as d3 from 'd3';
import { sankey, sankeyLinkHorizontal } from 'd3-sankey';

// Base dimensions for layout
export const LAYOUT_CONFIG = {
  width: 1600,
  height: 600,
  margin: {
    top: 20,
    right: 100,
    bottom: 20,
    left: 80,
  },
  nodeWidth: 32,
  nodePadding: 16, // Increased from 12 to prevent overlapping nodes
  minNodeHeight: 20,
};

/**
 * Get node colors from CSS variables (centralized theme system)
 */
export const getNodeColorsFromCSS = () => {
  const style = getComputedStyle(document.documentElement);
  const get = name => style.getPropertyValue(name).trim();

  return {
    ORG: get('--node-org') || '#6366f1',
    ASSET: get('--node-asset') || '#10b981',
    TECH: get('--node-tech') || '#f97316',
    CVE: get('--node-cve') || '#e53935',
    CWE: get('--node-cwe') || '#9c27b0',
    CAPEC: get('--node-capec') || '#00897b',
    TECHNIQUE: get('--node-technique') || '#1e88e5',
    APT: get('--node-apt') || '#f59e0b',
    DEFAULT: get('--node-default') || '#999',
  };
};

// Static fallback colors (used if CSS vars not available)
export const NODE_COLORS = {
  ORG: '#6366f1',
  ASSET: '#10b981',
  TECH: '#f97316',
  CVE: '#e53935',
  CWE: '#9c27b0',
  CAPEC: '#00897b',
  TECHNIQUE: '#1e88e5',
  APT: '#f59e0b',
  DEFAULT: '#999',
};

/**
 * Severity colors for CVE nodes based on CVSS score
 * Used for border/ring indicator
 */
export const SEVERITY_COLORS = {
  critical: { light: '#dc2626', dark: '#ef4444' }, // 9.0-10.0
  high: { light: '#ea580c', dark: '#f97316' }, // 7.0-8.9
  medium: { light: '#ca8a04', dark: '#eab308' }, // 4.0-6.9
  low: { light: '#16a34a', dark: '#22c55e' }, // 0.1-3.9
  none: { light: '#6b7280', dark: '#9ca3af' }, // 0 or unknown
};

/**
 * Get severity level from CVSS score or baseSeverity string
 */
export function getSeverityLevel(cvssScore, baseSeverity = null) {
  // First try CVSS score
  if (cvssScore !== null && cvssScore !== undefined && cvssScore !== '') {
    const score = parseFloat(cvssScore);
    if (!Number.isNaN(score)) {
      if (score >= 9.0) return 'critical';
      if (score >= 7.0) return 'high';
      if (score >= 4.0) return 'medium';
      if (score > 0) return 'low';
      return 'none';
    }
  }
  // Fallback to baseSeverity string
  if (baseSeverity) {
    const severity = String(baseSeverity).toUpperCase();
    if (severity === 'CRITICAL') return 'critical';
    if (severity === 'HIGH') return 'high';
    if (severity === 'MEDIUM') return 'medium';
    if (severity === 'LOW') return 'low';
  }
  return 'none';
}

/**
 * Get severity ring color for a CVE node
 */
export function getSeverityColor(cvssScore, isDark = false, baseSeverity = null) {
  const level = getSeverityLevel(cvssScore, baseSeverity);
  return isDark ? SEVERITY_COLORS[level].dark : SEVERITY_COLORS[level].light;
}

/**
 * Get node type from name
 */
export function getNodeType(name) {
  const nameLower = name.toLowerCase();
  if (nameLower.startsWith('org:')) return 'org';
  if (nameLower.startsWith('asset:')) return 'asset';
  if (nameLower.startsWith('tech:')) return 'tech';
  if (name.startsWith('CVE-') || name.startsWith('EXPOSED-') || name.startsWith('ASSET-')) {
    return 'cve';
  }
  if (name.startsWith('CWE-')) return 'cwe';
  if (name.startsWith('CAPEC-')) return 'capec';
  if (/^T\d+/.test(name)) return 'ttp';
  if (nameLower.startsWith('apt:')) return 'apt';
  return 'unknown';
}

/**
 * Get column index for node type (for fixed column layout)
 * Org=0, Asset=1, Tech=2, CVE=3, CWE=4, CAPEC=5, TTP=6, APT=7
 */
export function getNodeColumn(name) {
  const type = getNodeType(name);
  switch (type) {
  case 'org': return 0;
  case 'asset': return 1;
  case 'tech': return 2;
  case 'cve': return 3;
  case 'cwe': return 4;
  case 'capec': return 5;
  case 'ttp': return 6;
  case 'apt': return 7;
  default: return 3;
  }
}

/**
 * Get color for a node based on its type
 * Uses CSS variables for theme support
 */
export function getNodeColor(name) {
  const colors = getNodeColorsFromCSS();
  const nameLower = name.toLowerCase();
  if (nameLower.startsWith('org:')) {
    return colors.ORG || '#6366f1';
  }
  if (nameLower.startsWith('asset:')) {
    return colors.ASSET;
  }
  if (nameLower.startsWith('tech:')) {
    return colors.TECH;
  }
  if (name.startsWith('CVE-') || name.startsWith('EXPOSED-') || name.startsWith('ASSET-')) {
    return colors.CVE;
  }
  if (name.startsWith('CWE-')) {
    return colors.CWE;
  }
  if (name.startsWith('CAPEC-')) {
    return colors.CAPEC;
  }
  if (/^T\d+/.test(name)) {
    return colors.TECHNIQUE;
  }
  if (nameLower.startsWith('apt:')) {
    return colors.APT;
  }
  return colors.DEFAULT;
}

/**
 * Extract asset name from insights data
 */
export function extractAssetName(insightsData) {
  // Try to get the key from the root of insights data (e.g., "cynergy.app")
  if (insightsData) {
    const keys = Object.keys(insightsData);
    // Look for domain-like keys
    const domainKey = keys.find(k => k.includes('.') && !k.startsWith('_'));
    if (domainKey) return domainKey;
  }
  return null;
}

/**
 * Extract CVE list from insights data
 * Only returns items that start with "CVE-"
 */
export function extractCvesFromInsights(insightsData) {
  const cves = new Set();

  // From org_cves
  const orgCves = insightsData?.organization?.org_cves || [];
  orgCves.forEach(c => {
    // Only add items that start with CVE-
    if (c.name && c.name.startsWith('CVE-')) {
      cves.add(c.name);
    }
  });

  return Array.from(cves);
}

/**
 * Build attack paths map from insights data
 * Returns a map with CVE -> { cwes, capecs, techniques } chain
 */
export function buildAttackPathsMap(insightsData) {
  const map = {};

  // Extract from organization.org_cves with CWE/CAPEC chain
  const orgCves = insightsData?.organization?.org_cves || [];
  orgCves.forEach(cve => {
    const cwes = [];
    const capecs = [];
    const techniques = [];
    const cweToCapecs = {};
    const capecToTtps = {};

    // Process CWEs and their CAPECs
    if (cve.cwes && Array.isArray(cve.cwes)) {
      cve.cwes.forEach(cweData => {
        const cweId = cweData.name;
        if (cweId && !cwes.includes(cweId)) {
          cwes.push(cweId);
          cweToCapecs[cweId] = [];
        }

        // Process CAPECs from the CWE
        if (cweData.capec && Array.isArray(cweData.capec)) {
          cweData.capec.forEach(capecData => {
            // CAPEC data uses 'id' field, not 'name'
            const capecId = capecData.id || capecData.name;
            if (capecId) {
              if (!capecs.includes(capecId)) {
                capecs.push(capecId);
                capecToTtps[capecId] = [];
              }
              if (cweId && !cweToCapecs[cweId].includes(capecId)) {
                cweToCapecs[cweId].push(capecId);
              }

              // Process TTPs from the CAPEC
              if (capecData.ttps && Array.isArray(capecData.ttps)) {
                capecData.ttps.forEach(ttpData => {
                  const ttpId = ttpData.id;
                  if (ttpId) {
                    if (!techniques.includes(ttpId)) {
                      techniques.push(ttpId);
                    }
                    if (!capecToTtps[capecId].includes(ttpId)) {
                      capecToTtps[capecId].push(ttpId);
                    }
                  }
                });
              }
            }
          });
        }
      });
    }

    // Also include direct TTPs from CVE
    if (cve.ttps && Array.isArray(cve.ttps)) {
      cve.ttps.forEach(ttpData => {
        const ttpId = typeof ttpData === 'string' ? ttpData : ttpData.id;
        if (ttpId && !techniques.includes(ttpId)) {
          techniques.push(ttpId);
        }
      });
    }

    map[cve.name] = {
      cve: cve.name,
      attackVector: cve.attack_vector_name,
      potentialRisk: cve.potential_risk,
      cvss: cve.cvss,
      cwes,
      capecs,
      techniques,
      cweToCapecs,
      capecToTtps,
    };
  });

  return map;
}

/**
 * Build APT group to TTPs mapping
 */
export function buildAptGroupTtpsMap(aptGroups) {
  const map = {};
  (aptGroups || []).forEach(group => {
    const name = group.name || group['APT name'] || group.APT_group;
    if (name) {
      const ttps = group.TTPs || group.ttps || group.overlap_ttps || [];
      map[name] = new Set(ttps.map(t => {
        if (typeof t === 'string') {
          const match = t.match(/^(T\d+(?:\.\d+)?)/);
          return match ? match[1] : t;
        }
        return t.id || t;
      }));
    }
  });
  return map;
}

/**
 * Create sankey generator with fixed column layout based on node type
 * Columns: Org (0) → Asset (1) → Tech (2) → CVE (3) → CWE (4) → CAPEC (5) → TTP (6) → APT (7)
 * Handles gaps in columns by remapping to contiguous indices
 */
export function createSankeyGenerator(settings = {}, nodes = [], containerWidth = null, containerHeight = null) {
  const nodeWidth = settings.nodeWidth || LAYOUT_CONFIG.nodeWidth;
  const nodePadding = settings.nodePadding || LAYOUT_CONFIG.nodePadding;
  // Use container width if provided, otherwise fall back to config
  // Subtract margins to get the actual drawing width
  const effectiveWidth = containerWidth
    ? containerWidth - LAYOUT_CONFIG.margin.left - LAYOUT_CONFIG.margin.right
    : LAYOUT_CONFIG.width;
  const effectiveHeight = containerHeight
    ? containerHeight - LAYOUT_CONFIG.margin.top - LAYOUT_CONFIG.margin.bottom
    : LAYOUT_CONFIG.height;

  // Ensure nodes is an array
  const nodeArray = Array.isArray(nodes) ? nodes : [];

  // Get the logical column for a node name
  const getLogicalColumn = name => {
    if (!name) return 3;
    const nameLower = name.toLowerCase();
    if (nameLower.startsWith('org:')) return 0;
    if (nameLower.startsWith('asset:')) return 1;
    if (nameLower.startsWith('tech:')) return 2;
    if (name.startsWith('CVE-')) return 3;
    if (name.startsWith('CWE-')) return 4;
    if (name.startsWith('CAPEC-')) return 5;
    if (/^T\d+/.test(name)) return 6; // TTP/Technique
    if (nameLower.startsWith('apt:')) return 7;
    return 3; // Default to CVE column
  };

  // Count nodes per column to find the maximum
  const columnCounts = {};
  nodeArray.forEach(n => {
    if (n && n.name) {
      const col = getLogicalColumn(n.name);
      columnCounts[col] = (columnCounts[col] || 0) + 1;
    }
  });

  // Find the column with the most nodes
  const maxNodesInColumn = Math.max(...Object.values(columnCounts), 1);

  // Calculate dynamic height based on the column with most nodes
  // Each node needs space for proper visibility - increase multiplier for larger nodes
  const minHeightPerNode = 40; // Increased from previous value to make nodes larger
  const nodeBasedHeight = maxNodesInColumn * minHeightPerNode;
  const maxAllowedHeight = effectiveHeight * 4; // Increased from 2x to 4x to allow taller layouts
  const calculatedHeight = Math.min(
    Math.max(effectiveHeight, nodeBasedHeight),
    maxAllowedHeight,
  );

  // Find which logical columns are actually used
  const usedColumns = new Set(Object.keys(columnCounts).map(Number));

  // Create mapping from logical columns to contiguous indices
  const sortedColumns = Array.from(usedColumns).sort((a, b) => a - b);
  const columnMap = new Map();
  sortedColumns.forEach((col, idx) => {
    columnMap.set(col, idx);
  });

  // Calculate number of columns to determine spacing
  const numColumns = sortedColumns.length;

  // Force wider width to spread columns properly
  // Use a multiplier to ensure columns are well-spaced
  // Cap the multiplier to prevent excessive width with many columns
  const widthMultiplier = Math.min(Math.max(2.5, numColumns * 0.5), 5);
  const optimalWidth = effectiveWidth * widthMultiplier;

  // Use default alignment (left-aligned based on depth)
  // Use sankeyJustify to spread nodes vertically within their column
  return sankey()
    .nodeId(d => d.name)
    .nodeWidth(nodeWidth)
    .nodePadding(nodePadding)
    .nodeSort(null) // Don't sort nodes - keep them in natural order
    .extent([[0, 0], [optimalWidth, calculatedHeight]])
    .iterations(32);
}

/**
 * Reassign nodes to correct logical columns after d3-sankey layout
 * This fixes the issue where d3-sankey's automatic layout puts nodes in wrong columns
 */
export function reassignNodesToLogicalColumns(nodes, targetWidth) {
  if (!nodes || nodes.length === 0) return;

  // Define logical column for each node type
  const getLogicalColumn = name => {
    if (!name) return 3;
    const nameLower = name.toLowerCase();
    if (nameLower.startsWith('org:')) return 0;
    if (nameLower.startsWith('asset:')) return 1;
    if (nameLower.startsWith('tech:')) return 2;
    if (name.startsWith('CVE-')) return 3;
    if (name.startsWith('CWE-')) return 4;
    if (name.startsWith('CAPEC-')) return 5;
    if (/^T\d+/.test(name)) return 6; // TTP
    if (nameLower.startsWith('apt:')) return 7;
    return 3; // Default to CVE column
  };

  // Group nodes by their logical column
  const columnMap = new Map();
  nodes.forEach(node => {
    const col = getLogicalColumn(node.name);
    if (!columnMap.has(col)) {
      columnMap.set(col, []);
    }
    columnMap.get(col).push(node);
  });

  // Get sorted column indices
  const columns = Array.from(columnMap.keys()).sort((a, b) => a - b);
  const numColumns = columns.length;

  if (numColumns <= 1) return;

  // Calculate node width from first node
  const firstNode = nodes.find(n => n && n.x0 !== undefined && n.x1 !== undefined);
  if (!firstNode) {
    return;
  }
  const nodeWidth = firstNode.x1 - firstNode.x0;

  // Calculate even spacing
  const spacing = (targetWidth - nodeWidth) / (numColumns - 1);

  // Reassign x positions for each logical column
  columns.forEach((logicalCol, index) => {
    const newX = index * spacing;
    const nodesInColumn = columnMap.get(logicalCol);

    nodesInColumn.forEach(node => {
      const width = node.x1 - node.x0;
      // eslint-disable-next-line no-param-reassign
      node.x0 = newX;
      // eslint-disable-next-line no-param-reassign
      node.x1 = newX + width;
    });
  });
}

/**
 * Adjust node positions to spread columns evenly across available width
 * d3-sankey doesn't always respect extent width, so we manually adjust
 */
export function spreadColumnsEvenly(nodes, targetWidth) {
  if (!nodes || nodes.length === 0 || !targetWidth || targetWidth <= 0) {
    return;
  }

  // Group nodes by their x0 position (column)
  const columnMap = new Map();
  nodes.forEach(node => {
    if (!node || node.x0 === undefined || node.x1 === undefined) return;
    const col = node.x0;
    if (!columnMap.has(col)) {
      columnMap.set(col, []);
    }
    columnMap.get(col).push(node);
  });

  // Get unique column positions and sort them
  const columns = Array.from(columnMap.keys()).sort((a, b) => a - b);
  const numColumns = columns.length;

  if (numColumns <= 1) return;

  // Calculate even spacing across target width
  const firstNode = nodes.find(n => n && n.x0 !== undefined && n.x1 !== undefined);
  if (!firstNode) {
    return;
  }

  const nodeWidth = firstNode.x1 - firstNode.x0;
  if (Number.isNaN(nodeWidth) || nodeWidth <= 0) {
    return;
  }

  const spacing = (targetWidth - nodeWidth) / (numColumns - 1);
  if (Number.isNaN(spacing)) {
    return;
  }

  // Reassign x positions for each column
  columns.forEach((oldX, index) => {
    const newX = index * spacing;
    if (Number.isNaN(newX)) return;

    const nodesInColumn = columnMap.get(oldX);
    nodesInColumn.forEach(node => {
      if (!node || node.x0 === undefined || node.x1 === undefined) return;
      const width = node.x1 - node.x0;
      if (Number.isNaN(width)) return;

      // eslint-disable-next-line no-param-reassign
      node.x0 = newX;
      // eslint-disable-next-line no-param-reassign
      node.x1 = newX + width;
    });
  });
}

/**
 * Generate link path for sankey diagram using d3-sankey's built-in generator
 * This properly distributes links across the full height of nodes
 */
export function generateLinkPath(d) {
  return sankeyLinkHorizontal()(d);
}

/**
 * Generate standard Sankey link path
 * Uses d3-sankey's built-in path generator for proper flow visualization
 * This ensures links properly fill the full height of nodes
 */
export function generateGradientLinkPath(d) {
  return sankeyLinkHorizontal()(d);
}

/**
 * Update link positions after node drag
 */
export function updateLinkPositions(links) {
  const sourceLinks = {};
  const targetLinks = {};

  links.forEach(link => {
    const sk = link.source.name;
    const tk = link.target.name;
    if (!sourceLinks[sk]) sourceLinks[sk] = [];
    if (!targetLinks[tk]) targetLinks[tk] = [];
    sourceLinks[sk].push(link);
    targetLinks[tk].push(link);
  });

  Object.keys(sourceLinks).forEach(key => {
    const nodeLinks = sourceLinks[key];
    const node = nodeLinks[0].source;
    const totalWidth = nodeLinks.reduce((s, l) => s + l.width, 0);
    let currentY = node.y0 + (node.y1 - node.y0 - totalWidth) / 2;
    nodeLinks.forEach(link => {
      Object.assign(link, { y0: currentY + link.width / 2 });
      currentY += link.width;
    });
  });

  Object.keys(targetLinks).forEach(key => {
    const nodeLinks = targetLinks[key];
    const node = nodeLinks[0].target;
    const totalWidth = nodeLinks.reduce((s, l) => s + l.width, 0);
    let currentY = node.y0 + (node.y1 - node.y0 - totalWidth) / 2;
    nodeLinks.forEach(link => {
      Object.assign(link, { y1: currentY + link.width / 2 });
      currentY += link.width;
    });
  });
}
/**
 * Get connected nodes and links for a given node
 */
export function getConnectedElements(nodeName, links) {
  const connectedNodes = new Set([nodeName]);
  const connectedLinks = new Set();

  const traverseForward = name => {
    links.forEach((link, i) => {
      if (link.source.name === name && !connectedLinks.has(i)) {
        connectedLinks.add(i);
        connectedNodes.add(link.target.name);
        traverseForward(link.target.name);
      }
    });
  };

  const traverseBackward = name => {
    links.forEach((link, i) => {
      if (link.target.name === name && !connectedLinks.has(i)) {
        connectedLinks.add(i);
        connectedNodes.add(link.source.name);
        traverseBackward(link.source.name);
      }
    });
  };

  traverseForward(nodeName);
  traverseBackward(nodeName);

  return { connectedNodes, connectedLinks };
}

/**
 * Aggregate duplicate links
 */
export function aggregateLinks(links) {
  const map = {};
  links.forEach(l => {
    const key = `${l.source}|${l.target}`;
    if (!map[key]) map[key] = { ...l };
    else map[key].value += l.value;
  });
  return Object.values(map);
}

/**
 * Calculate center transform for fullscreen mode
 */
export function calculateCenterTransform(containerWidth, containerHeight, nodeCount = 0) {
  const minHeightPerNode = LAYOUT_CONFIG.minNodeHeight + LAYOUT_CONFIG.nodePadding;
  const calculatedHeight = Math.max(
    LAYOUT_CONFIG.height,
    nodeCount * minHeightPerNode * 0.4,
  );
  const offsetX = Math.max(0, (containerWidth - LAYOUT_CONFIG.width) / 2);
  const offsetY = Math.max(0, (containerHeight - calculatedHeight) / 2);
  return d3.zoomIdentity.translate(offsetX, offsetY);
}
